import React, { useEffect, useState, useCallback } from 'react';
import { useAuth, getUserRole } from '../contexts/AuthContext';
import VisaoRestritaBanner from '../components/VisaoRestritaBanner';
import { withSetorScope, isFiscalSetorial, getFiscalSetor } from '../utils/dataIsolation';
import {
  Box,
  Typography,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Chip,
  IconButton,
  Alert,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddIcon from '@mui/icons-material/Add';

import ScheduleIcon from '@mui/icons-material/Schedule';
import DownloadIcon from '@mui/icons-material/Download';
import API_CONFIG from '../config/api';
import { apiFetch, getToken } from '../api/client';
import { getUnitStorageJSON, setUnitStorageJSON } from '../utils/storageByUnit';

const BACKEND_URL = API_CONFIG.BACKEND_URL;

const withUnidade = (path, unidade) => {
  if (!unidade?.codigo) return path;
  return path.includes('?')
    ? `${path}&unidade=${encodeURIComponent(unidade.codigo)}`
    : `${path}?unidade=${encodeURIComponent(unidade.codigo)}`;
};

function isSetorDiretoriaTecnica(setor) {
  const s = String(setor || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, ' ')
    .trim();
  return s.includes('DIRETORIA TECNICA');
}

function isContratoServicoMedico(contrato) {
  return (
    contrato?.servicoMedico === 1 ||
    contrato?.servicoMedico === true ||
    contrato?.servicoMedico === '1'
  );
}

function especialidadeContrato(contrato) {
  const col = String(contrato?.especialidade || '').trim();
  if (col) return col;
  const m = String(contrato?.empresa || '').match(/\(\s*([^)]+)\s*\)/);
  return m ? m[1].trim() : '—';
}

function empresaSemEspParentese(contrato) {
  if (!isContratoServicoMedico(contrato)) return contrato.empresa;
  // Se já tem coluna, preferir nome sem ( ); ORALMED mantém nome original no banco
  if (contrato?.especialidade && /\(\s*[^)]+\s*\)/.test(contrato.empresa || '')) {
    return String(contrato.empresa)
      .replace(/\s*\([^)]*\)\s*/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }
  return contrato.empresa;
}

// Abrir PDF com token (evita "Não autorizado" ao abrir em nova aba)
async function openPdfComAuth(url, unidadeCodigo) {
  const token = getToken();
  try {
    // Preferência: nova aba com access_token (browser faz stream; evita Failed to fetch em PDF grande)
    const abs = new URL(url, window.location.origin);
    if (token) abs.searchParams.set('access_token', token);
    if (unidadeCodigo && !abs.searchParams.has('unidade')) {
      abs.searchParams.set('unidade', unidadeCodigo);
    }
    const openUrl = abs.toString();

    const win = window.open(openUrl, '_blank', 'noopener,noreferrer');
    if (win) return;

    // Fallback se popup bloqueado: fetch + blob
    const res = await fetch(openUrl, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      alert(data.error || 'Erro ao abrir PDF. Faça login novamente.');
      return;
    }
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    window.open(blobUrl, '_blank');
    setTimeout(() => URL.revokeObjectURL(blobUrl), 120000);
  } catch (e) {
    console.error('[openPdfComAuth]', e);
    alert('Erro ao abrir PDF.');
  }
}

const cargos = [
  'Diretor Presidente',
  'Diretor Financeiro',
  'Diretor(a) Técnica Operacional',
  'Diretoria Executiva',
  'Gerente Corporativo de Contratos',
];

const initialForm = {
  nome: '',
  email: '',
  setor: '',
};

const initialFormDiretor = {
  nome: '',
  email: '',
  cargo: '',
};

const initialFormContratoGestao = {
  numeroContrato: '',
  numeroAditivo: '',
  valorTotalRecebido: '',
  valorDestinadoSetor: '',
};

export default function FiscaisPage() {
  const { unidadeSelecionada, user, rbacRole, fiscalSetor } = useAuth();
  const visaoFiscal = isFiscalSetorial(user, rbacRole || getUserRole(user));
  const setorFiscal = getFiscalSetor(user, fiscalSetor);
  const scopeOpts = { user, rbacRole: rbacRole || getUserRole(user), fiscalSetor };
  // Estados para a visão geral dos fiscais e contratos
  const [prestadores, setPrestadores] = useState([]);
  const [fiscais, setFiscais] = useState([]);
  const [setores, setSetores] = useState([]);
  const [aditivosPorContrato, setAditivosPorContrato] = useState({});
  const [avaliacoesPorContrato, setAvaliacoesPorContrato] = useState({});
  const [termosDesignacaoPorContrato, setTermosDesignacaoPorContrato] = useState({});
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(false);

  // Estados para os modais existentes
  const [diretores, setDiretores] = useState([]);
  const [contratosGestao, setContratosGestao] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [fiscaisModalOpen, setFiscaisModalOpen] = useState(false);
  const [diretoresModalOpen, setDiretoresModalOpen] = useState(false);
  const [contratoGestaoModalOpen, setContratoGestaoModalOpen] = useState(false);
  const [formDiretor, setFormDiretor] = useState(initialFormDiretor);
  const [formContratoGestao, setFormContratoGestao] = useState(initialFormContratoGestao);

  // Buscar prestadores
  const fetchPrestadores = useCallback(async () => {
    setLoading(true);
    try {
      // TODO Backend/LGPD Fase 5: ?setor= no GET /api/prestadores
      let url = unidadeSelecionada 
        ? `${BACKEND_URL}/api/prestadores?unidade=${encodeURIComponent(unidadeSelecionada.codigo)}`
        : `${BACKEND_URL}/api/prestadores`;
      url = withSetorScope(url, scopeOpts);
      
      const res = await fetch(url);
      if (!res.ok) throw new Error('Erro ao buscar prestadores');
      let data = await res.json();
      // Forçar eventual, corporativo e finalizado para booleano
      data = data.map((p) => ({
        ...p,
        eventual: p.eventual === 1 || p.eventual === '1' || p.eventual === true || p.eventual === 'true',
        corporativo: p.corporativo === 1 || p.corporativo === '1' || p.corporativo === true || p.corporativo === 'true',
        finalizado: p.finalizado === 1 || p.finalizado === '1' || p.finalizado === true || p.finalizado === 'true',
      }));
      if (visaoFiscal && setorFiscal) {
        data = data.filter((p) => p.setor === setorFiscal);
      }
      setPrestadores(data);
    } catch (error) {
      console.error('Erro ao buscar prestadores:', error);
      setPrestadores([]);
    } finally {
      setLoading(false);
    }
  }, [unidadeSelecionada, visaoFiscal, setorFiscal, user, rbacRole, fiscalSetor]);

  // Buscar fiscais
  const fetchFiscais = useCallback(async () => {
    try {
      // TODO Backend/LGPD Fase 5: ?setor= no GET /api/fiscais
      let url = unidadeSelecionada 
        ? `${BACKEND_URL}/api/fiscais?unidade=${encodeURIComponent(unidadeSelecionada.codigo)}`
        : `${BACKEND_URL}/api/fiscais`;
      url = withSetorScope(url, scopeOpts);
      
      const res = await fetch(url);
      if (!res.ok) throw new Error('Erro ao buscar fiscais');
      let data = await res.json();
      if (visaoFiscal && setorFiscal) {
        data = (Array.isArray(data) ? data : []).filter((f) => f.setor === setorFiscal);
      }
      setFiscais(Array.isArray(data) ? data : []);
    } catch {
      setFiscais([]);
    }
  }, [unidadeSelecionada, visaoFiscal, setorFiscal, user, rbacRole, fiscalSetor]);

  // Buscar setores do backend (tabela setores)
  const fetchSetores = useCallback(async () => {
    try {
      const res = await apiFetch(withUnidade('/api/setores', unidadeSelecionada));
      if (!res.ok) throw new Error('Erro ao buscar setores');
      const data = await res.json();
      const nomes = data
        .filter((s) => s.ativo === undefined || s.ativo === 1)
        .map((s) => s.nome)
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b));
      setSetores(nomes);
    } catch (error) {
      console.error('Erro ao buscar setores:', error);
      setSetores([]);
    }
  }, [unidadeSelecionada]);

  const fetchContratosGestao = useCallback(async () => {
    try {
      // Adicionar filtro de unidade na URL
      const url = unidadeSelecionada 
        ? `${BACKEND_URL}/api/contratos-gestao?unidade=${encodeURIComponent(unidadeSelecionada.codigo)}`
        : `${BACKEND_URL}/api/contratos-gestao`;
      
      const res = await fetch(url);
      if (!res.ok) throw new Error('Erro ao buscar contratos de gestão');
      const data = await res.json();
      setContratosGestao(data);
    } catch (error) {
      console.error('Erro ao buscar contratos de gestão:', error);
      setContratosGestao([]);
    }
  }, [unidadeSelecionada]);

  // Buscar aditivos para contratos
  const fetchAditivosParaContratos = async (contratos) => {
    const result = {};
    await Promise.all(
      contratos.map(async (contrato) => {
        try {
          const res = await apiFetch(
            `/api/aditivos?numeroContrato=${encodeURIComponent(contrato.numeroContrato)}${
              unidadeSelecionada?.codigo ? `&unidade=${encodeURIComponent(unidadeSelecionada.codigo)}` : ''
            }`
          );
          if (res.ok) {
            const aditivos = await res.json();
            result[contrato.numeroContrato] = aditivos;
          } else {
            result[contrato.numeroContrato] = [];
          }
        } catch {
          result[contrato.numeroContrato] = [];
        }
      })
    );
    return result;
  };

  // Buscar avaliações para contratos
  const fetchAvaliacoesParaContratos = async (contratos) => {
    const result = {};
    console.log('Buscando avaliações para contratos:', contratos.map(c => c.numeroContrato));
    
    try {
      // Buscar todas as avaliações de uma vez
      const unidadeQs = unidadeSelecionada?.codigo
        ? `?unidade=${encodeURIComponent(unidadeSelecionada.codigo)}`
        : '';
      const res = await apiFetch(`/api/avaliacoes${unidadeQs}`);
      console.log('Resposta das avaliações:', res.status, res.statusText);
      
      if (res.ok) {
        const todasAvaliacoes = await res.json();
        console.log('Todas as avaliações encontradas:', todasAvaliacoes);
        
        // Agrupar avaliações por número de contrato
        contratos.forEach(contrato => {
          const avaliacoesContrato = todasAvaliacoes.filter(av => av.numeroContrato === contrato.numeroContrato);
          result[contrato.numeroContrato] = avaliacoesContrato;
          console.log(`Avaliações para ${contrato.numeroContrato}:`, avaliacoesContrato);
        });
      } else {
        console.log('Erro ao buscar avaliações');
        contratos.forEach(contrato => {
          result[contrato.numeroContrato] = [];
        });
      }
    } catch (error) {
      console.error('Erro ao buscar avaliações:', error);
      contratos.forEach(contrato => {
        result[contrato.numeroContrato] = [];
      });
    }
    
    console.log('Resultado final das avaliações:', result);
    return result;
  };

  // Buscar termos de designação para contratos
  const fetchTermosDesignacaoParaContratos = async (contratos) => {
    const result = {};
    console.log('Buscando termos de designação para contratos:', contratos.map(c => c.numeroContrato));
    
    try {
      // Buscar todos os termos de designação de uma vez
      const unidadeQs = unidadeSelecionada?.codigo
        ? `?unidade=${encodeURIComponent(unidadeSelecionada.codigo)}`
        : '';
      const res = await apiFetch(`/api/termos-designacao${unidadeQs}`);
      console.log('Resposta dos termos de designação:', res.status, res.statusText);
      
      if (res.ok) {
        const todosTermos = await res.json();
        console.log('Todos os termos de designação encontrados:', todosTermos);
        
        // Agrupar termos por número de contrato (somente da unidade selecionada)
        const unidadeAtual = String(unidadeSelecionada?.codigo || '').trim().toUpperCase();
        contratos.forEach(contrato => {
          const termosContrato = todosTermos.filter((termo) => {
            if (termo.numero_contrato !== contrato.numeroContrato) return false;
            const uTermo = String(termo.unidade || '').trim().toUpperCase();
            // Sem unidade no registro = legado ambíguo: não exibir (evita vazamento multiunidade)
            if (!uTermo) return false;
            if (unidadeAtual && uTermo !== unidadeAtual) return false;
            return true;
          });
          result[contrato.numeroContrato] = termosContrato;
          console.log(`Termos para ${contrato.numeroContrato}:`, termosContrato);
        });
      } else {
        console.log('Erro ao buscar termos de designação');
        contratos.forEach(contrato => {
          result[contrato.numeroContrato] = [];
        });
      }
    } catch (error) {
      console.error('Erro ao buscar termos de designação:', error);
      contratos.forEach(contrato => {
        result[contrato.numeroContrato] = [];
      });
    }
    
    console.log('Resultado final dos termos de designação:', result);
    return result;
  };

  useEffect(() => {
    fetchPrestadores();
    fetchFiscais();
    fetchContratosGestao();
    fetchSetores();
    
    // Carregar dados salvos do localStorage (apenas para diretores por enquanto)
    const diretoresSalvos = getUnitStorageJSON('diretores', unidadeSelecionada?.codigo, []);
    setDiretores(Array.isArray(diretoresSalvos) ? diretoresSalvos : []);
  }, [fetchPrestadores, fetchFiscais, fetchContratosGestao, fetchSetores, unidadeSelecionada?.codigo]);

  useEffect(() => {
    const fetchAditivos = async () => {
      if (prestadores.length > 0) {
        const aditivos = await fetchAditivosParaContratos(prestadores);
        setAditivosPorContrato(aditivos);
      }
    };
    fetchAditivos();
  }, [prestadores]);

  useEffect(() => {
    const fetchAvaliacoes = async () => {
      if (prestadores.length > 0) {
        const avaliacoes = await fetchAvaliacoesParaContratos(prestadores);
        setAvaliacoesPorContrato(avaliacoes);
      }
    };
    fetchAvaliacoes();
  }, [prestadores]);

  useEffect(() => {
    const fetchTermosDesignacao = async () => {
      if (prestadores.length > 0) {
        const termos = await fetchTermosDesignacaoParaContratos(prestadores);
        setTermosDesignacaoPorContrato(termos);
      }
    };
    fetchTermosDesignacao();
  }, [prestadores]);

  // Mapeamento dinâmico setor -> fiscal baseado nos dados do backend (unidade atual)
  const setorParaFiscal = {};
  fiscais.forEach(fiscal => {
    setorParaFiscal[fiscal.setor] = fiscal.nome;
  });

  const getFiscalName = (setor) => {
    return setorParaFiscal[setor] || 'Fiscal não cadastrado';
  };

  // Agrupar prestadores por setor
  const setoresUnicos = Array.from(new Set(prestadores.map(p => p.setor))).filter(Boolean).sort((a, b) => a.localeCompare(b));
  const prestadoresPorSetor = {};
  setoresUnicos.forEach(setor => {
    prestadoresPorSetor[setor] = prestadores.filter(p => p.setor === setor);
  });

  // Calcular dias para vencimento
  const diasParaVencer = (dataFim) => {
    if (!dataFim) return '-';
    
    let vencimento;
    
    // Se a data está no formato DD/MM/YYYY, converte para o formato correto
    if (dataFim.includes('/') && dataFim.split('/').length === 3) {
      const [day, month, year] = dataFim.split('/');
      if (day && month && year) {
        // Cria a data no formato YYYY-MM-DD para evitar inversão
        vencimento = new Date(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
      }
    } else if (dataFim.includes('-') && dataFim.split('-').length === 3) {
      // Se a data está no formato YYYY-MM-DD, usa diretamente
      vencimento = new Date(dataFim);
    } else {
      vencimento = new Date(dataFim);
    }
    
    if (isNaN(vencimento.getTime())) return '-';
    
    const hoje = new Date();
    const diffTime = vencimento - hoje;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Calcular valor total por setor
  const calcularValorTotalSetor = (contratos) => {
    return contratos
      .filter(c => !c.finalizado)
      .reduce((acc, c) => {
        // Limpar formatação de moeda e converter para número
        let valor = c.valorMes || '0';
        if (typeof valor === 'string') {
          // Remove R$, espaços
          valor = valor.replace(/[R$\s]/g, '');
          
          // Se tem vírgula, é formato brasileiro - converte para ponto decimal
          if (valor.includes(',')) {
            // Remove pontos (separadores de milhares) e converte vírgula para ponto
            valor = valor.replace(/\./g, '').replace(',', '.');
          } else if (valor.includes('.')) {
            // Se tem ponto, verifica se é separador decimal ou de milhares
            const partes = valor.split('.');
            if (partes.length === 2 && partes[1].length <= 2) {
            // É separador decimal, mantém como está
            } else {
              // É separador de milhares, remove
              valor = valor.replace(/\./g, '');
            }
          }
        }
        const numValor = parseFloat(valor) || 0;
        
        // Debug log para verificar valores
        if (c.setor === 'Jurídico' || c.setor === 'Financeiro') {
          console.log(`Contrato ${c.numeroContrato} - Valor original: ${c.valorMes}, Valor limpo: ${valor}, Valor numérico: ${numValor}`);
        }
        
        return acc + numValor;
      }, 0);
  };

  // Função para obter descrição da nota

  // Função para obter cor da nota
  const getNotaColor = (nota) => {
    if (nota >= 1 && nota < 2) return 'error';
    if (nota >= 2 && nota < 3) return 'warning';
    if (nota >= 3 && nota < 4) return 'info';
    if (nota >= 4 && nota < 5) return 'success';
    if (nota === 5) return 'success';
    return 'default';
  };

  // Formatar valor
  const formatCurrency = (valor) => {
    if (!valor) return 'R$ 0,00';
    
    // Se o valor já está formatado como string, limpar e converter
    if (typeof valor === 'string') {
      // Remove formatação existente (R$, espaços)
      let valorLimpo = valor.replace(/[R$\s]/g, '');
      
      // Se tem vírgula, é formato brasileiro - converte para ponto decimal
      if (valorLimpo.includes(',')) {
        // Remove pontos (separadores de milhares) e converte vírgula para ponto
        valorLimpo = valorLimpo.replace(/\./g, '').replace(',', '.');
      } else if (valorLimpo.includes('.')) {
        // Se tem ponto, verifica se é separador decimal ou de milhares
        const partes = valorLimpo.split('.');
        if (partes.length === 2 && partes[1].length <= 2) {
          // É separador decimal, mantém como está
        } else {
          // É separador de milhares, remove
          valorLimpo = valorLimpo.replace(/\./g, '');
        }
      }
      
      const numValor = parseFloat(valorLimpo);
      if (isNaN(numValor)) return 'R$ 0,00';
      
      // Formatar corretamente para moeda brasileira
      return numValor.toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      });
    }
    
    // Se é número, formatar diretamente
    return valor.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    });
  };

  // Formatar data para exibição
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    
    // Se a data está no formato DD/MM/YYYY, retorna como está
    if (dateString.includes('/') && dateString.split('/').length === 3) {
      return dateString; // Já está no formato correto
    }
    
    // Se a data está no formato YYYY-MM-DD, converte para DD/MM/YYYY
    if (dateString.includes('-') && dateString.split('-').length === 3) {
      const [year, month, day] = dateString.split('-');
      return `${day}/${month}/${year}`;
    }
    
    // Fallback para o comportamento original
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString('pt-BR');
  };

  // Abrir PDF do contrato
  const abrirPDFContrato = (contrato) => {
    const url = `${BACKEND_URL}/api/prestadores/${contrato.id}/contrato/pdf`;
    openPdfComAuth(url, unidadeSelecionada?.codigo);
  };

  // Abrir PDF do aditivo
  const abrirPDFAditivo = (aditivo) => {
    if (aditivo.pdfPath) {
      openPdfComAuth(`${BACKEND_URL}/api/aditivos/${aditivo.id}/pdf`, unidadeSelecionada?.codigo);
    } else {
      alert('Nenhum PDF cadastrado para este aditivo.');
    }
  };

  // Abrir PDF da avaliação
  const abrirPDFAvaliacao = (avaliacao) => {
    if (avaliacao.pdfPath) {
      openPdfComAuth(`${BACKEND_URL}/api/avaliacoes/${avaliacao.id}/pdf`, unidadeSelecionada?.codigo);
    } else {
      alert('PDF não disponível para esta avaliação');
    }
  };

  // Abrir PDF do termo de designação
  const abrirPDFTermoDesignacao = (termo) => {
    if (termo.pdf_path) {
      openPdfComAuth(`${BACKEND_URL}/api/termos-designacao/${termo.id}/pdf`, unidadeSelecionada?.codigo);
    } else {
      alert('PDF não disponível para este termo de designação');
    }
  };

  // Funções existentes para os modais
  const handleOpenModal = (fiscal = null) => {
    if (fiscal) {
      setForm({
        nome: fiscal.nome,
        email: fiscal.email,
        setor: fiscal.setor,
      });
      setEditId(fiscal.id);
    } else {
      setForm(initialForm);
      setEditId(null);
    }
    setModalOpen(true);
    setFormError('');
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setForm(initialForm);
    setFormError('');
    setEditId(null);
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSave = async () => {
    console.log('🔍 handleSave chamado com form:', form);
    
    if (!form.nome || !form.email || !form.setor) {
      console.log('❌ Campos obrigatórios não preenchidos');
      setFormError('Todos os campos são obrigatórios.');
      return;
    }

    if (!unidadeSelecionada?.codigo) {
      setFormError('Selecione uma unidade antes de salvar.');
      return;
    }

    setSaving(true);
    setFormError('');

    try {
      const path = editId ? `/api/fiscais/${editId}` : '/api/fiscais';
      const method = editId ? 'PUT' : 'POST';

      const dadosFiscal = {
        ...form,
        unidade: unidadeSelecionada.codigo,
      };

      const res = await apiFetch(path, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(dadosFiscal),
      });

      console.log('📊 Status da resposta:', res.status);
      console.log('📋 Headers:', res.headers);

      if (!res.ok) {
        const errorText = await res.text();
        console.error('❌ Erro na resposta:', errorText);
        throw new Error(`Erro ao salvar fiscal: ${res.status} - ${errorText}`);
      }

      const result = await res.json();
      console.log('✅ Fiscal salvo com sucesso:', result);

      await fetchFiscais();
      handleCloseModal();
    } catch (error) {
      console.error('❌ Erro ao salvar fiscal:', error);
      setFormError(`Erro ao salvar fiscal: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    console.log('🗑️ Tentando deletar fiscal com ID:', deleteId);
    
    try {
      const res = await apiFetch(withUnidade(`/api/fiscais/${deleteId}`, unidadeSelecionada), {
        method: 'DELETE',
      });

      console.log('📊 Status da resposta:', res.status);

      if (!res.ok) {
        const errorText = await res.text();
        console.error('❌ Erro na resposta:', errorText);
        throw new Error(`Erro ao deletar fiscal: ${res.status} - ${errorText}`);
      }

      const result = await res.json();
      console.log('✅ Fiscal deletado com sucesso:', result);

      await fetchFiscais();
      closeDeleteDialog();
    } catch (error) {
      console.error('❌ Erro ao deletar fiscal:', error);
      alert(`Erro ao deletar fiscal: ${error.message}`);
    }
  };

  const openDeleteDialog = (id) => {
    setDeleteId(id);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setDeleteId(null);
    setDeleteDialogOpen(false);
  };

  // Funções para diretores e contratos de gestão
  const handleSaveDiretor = () => {
    if (!formDiretor.nome || !formDiretor.email || !formDiretor.cargo) {
      alert('Todos os campos são obrigatórios.');
      return;
    }

    const novoDiretor = {
      id: Date.now(),
      ...formDiretor
    };

    const diretoresAtualizados = [...diretores, novoDiretor];
    setDiretores(diretoresAtualizados);
    setUnitStorageJSON('diretores', unidadeSelecionada?.codigo, diretoresAtualizados);
    
    setFormDiretor(initialFormDiretor);
    setDiretoresModalOpen(false);
  };

  const handleSaveContratoGestao = async () => {
    if (!formContratoGestao.numeroContrato || !formContratoGestao.valorTotalRecebido) {
      alert('Número do contrato e valor total recebido são obrigatórios.');
      return;
    }

    if (!unidadeSelecionada?.codigo) {
      alert('Selecione uma unidade antes de salvar.');
      return;
    }

    try {
      const response = await apiFetch('/api/contratos-gestao', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          numero_contrato: formContratoGestao.numeroContrato,
          numero_aditivo: formContratoGestao.numeroAditivo,
          valor_total_recebido: parseFloat(formContratoGestao.valorTotalRecebido),
          valor_destinado_setor: formContratoGestao.valorDestinadoSetor ? parseFloat(formContratoGestao.valorDestinadoSetor) : null,
          unidade: unidadeSelecionada.codigo,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Erro ao salvar contrato de gestão');
      }

      // Recarregar lista de contratos
      fetchContratosGestao();
      
      setFormContratoGestao(initialFormContratoGestao);
      setContratoGestaoModalOpen(false);
      
      alert('Contrato de gestão salvo com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar contrato de gestão:', error);
      alert(`Erro ao salvar contrato de gestão: ${error.message}`);
    }
  };

  // Combina todos os dados para busca
  const allData = [
    ...fiscais.map(f => ({ ...f, tipo: 'fiscal' })),
    ...diretores.map(d => ({ ...d, tipo: 'diretor' })),
    ...contratosGestao.map(c => ({ ...c, tipo: 'contrato' }))
  ];

  const filteredData = allData.filter(item =>
    item.nome?.toLowerCase().includes(search.toLowerCase()) ||
    item.email?.toLowerCase().includes(search.toLowerCase()) ||
    item.setor?.toLowerCase().includes(search.toLowerCase()) ||
    item.cargo?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Box sx={{ p: 3 }}>
      <VisaoRestritaBanner />

      {/* Barra de busca */}
      <TextField
        fullWidth
        placeholder="Buscar por setor, nome do fiscal ou contrato..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{
          mb: 3,
          '& .MuiOutlinedInput-root': {
            borderRadius: 2,
            background: 'rgba(255,255,255,0.9)',
            backdropFilter: 'blur(10px)',
            '&:hover': {
              background: 'rgba(255,255,255,0.95)'
            }
          }
        }}
      />

      {/* Cards dos setores */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          setoresUnicos
            .filter(setor =>
              search.trim() === '' ||
              setor.toLowerCase().includes(search.toLowerCase()) ||
              getFiscalName(setor).toLowerCase().includes(search.toLowerCase()) ||
              prestadoresPorSetor[setor].some(p =>
                p.empresa.toLowerCase().includes(search.toLowerCase()) ||
                p.numeroContrato.toLowerCase().includes(search.toLowerCase())
              )
            )
            .map((setor, idx) => {
              const contratos = prestadoresPorSetor[setor];
              const valorTotal = calcularValorTotalSetor(contratos);
              const mostrarColsMedicos =
                isSetorDiretoriaTecnica(setor) &&
                contratos.some((c) => isContratoServicoMedico(c));
              
              return (
                <Card
                  key={idx}
                  sx={{
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(240,248,255,0.9) 100%)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: 3,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                    border: '1px solid rgba(255,255,255,0.3)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 12px 40px rgba(0,0,0,0.15)'
                    }
                  }}
                >
                  <Accordion
                    expanded={expanded === setor}
                    onChange={() => setExpanded(expanded === setor ? null : setor)}
                    sx={{
                      background: 'transparent',
                      '&:before': { display: 'none' },
                      '&.Mui-expanded': { margin: 0 }
                    }}
                  >
                    <AccordionSummary
                      sx={{
                        background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                        borderRadius: 2,
                        color: 'white',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)'
                        }
                      }}
                    >
                      <Box display="flex" alignItems="center" width="100%">
                        <PersonIcon sx={{ mr: 2, color: 'white' }} />
                        <Box flex={1}>
                          <Typography variant="h6" fontWeight={700}>
                            {setor}
                          </Typography>
                          <Typography variant="body2" sx={{ opacity: 0.9 }}>
                            {getFiscalName(setor)}
                          </Typography>
                        </Box>
                        <Box textAlign="right" mr={2}>
                          <Typography variant="body2" fontWeight={600}>
                            {contratos.length} {contratos.length === 1 ? 'contrato' : 'contratos'}
                          </Typography>
                          <Typography variant="body2" sx={{ opacity: 0.9 }}>
                            {formatCurrency(valorTotal)}
                          </Typography>
                        </Box>
                        <ExpandMoreIcon sx={{ color: 'white' }} />
                      </Box>
                    </AccordionSummary>
                    
                    <AccordionDetails sx={{ p: 0 }}>
                      {contratos.length > 0 && (
                        <TableContainer component={Paper} sx={{ mt: 2, borderRadius: 2 }}>
                          <Table>
                            <TableHead>
                              <TableRow sx={{ background: 'rgba(25,118,210,0.1)' }}>
                                <TableCell sx={{ fontWeight: 600 }}>Empresa</TableCell>
                                {mostrarColsMedicos && (
                                  <>
                                    <TableCell sx={{ fontWeight: 600 }}>Especialidade</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }} align="right">
                                      Horas mensais
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 600 }} align="right">
                                      Qtd. médicos
                                    </TableCell>
                                  </>
                                )}
                                <TableCell sx={{ fontWeight: 600 }}>N° Contrato</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Aditivos</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Valor Mensal</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Vencimento</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Dias</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Avaliação de Fornecedor</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Termo de Designação</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {contratos.map((contrato, i) => {
                                const aditivos = aditivosPorContrato[contrato.numeroContrato] || [];
                                const dias = diasParaVencer(contrato.vigenciaFim);
                                const medico = isContratoServicoMedico(contrato);
                                
                                return (
                                  <TableRow 
                                    key={i}
                                    sx={{
                                      backgroundColor: contrato.finalizado ? 'rgba(0,0,0,0.05)' : 'transparent',
                                      '&:hover': {
                                        backgroundColor: 'rgba(25,118,210,0.05)'
                                      }
                                    }}
                                  >
                                    <TableCell>
                                      <Box display="flex" alignItems="center" gap={1}>
                                        <Typography variant="body2">
                                          {empresaSemEspParentese(contrato)}
                                          {contrato.finalizado && (
                                            <Chip 
                                              label="Desativado" 
                                              size="small" 
                                              color="default" 
                                              sx={{ ml: 1 }}
                                            />
                                          )}
                                        </Typography>
                                        {contrato.eventual && (
                                          <Chip
                                            icon={<ScheduleIcon />}
                                            label="Eventual"
                                            size="small"
                                            sx={{
                                              backgroundColor: '#FF9500',
                                              color: 'white',
                                              '& .MuiChip-icon': { color: 'white' }
                                            }}
                                          />
                                        )}
                                        {contrato.corporativo && (
                                          <Chip
                                            icon={<BusinessIcon />}
                                            label="Corporativo"
                                            size="small"
                                            sx={{
                                              backgroundColor: '#1976d2',
                                              color: 'white',
                                              '& .MuiChip-icon': { color: 'white' }
                                            }}
                                          />
                                        )}
                                      </Box>
                                    </TableCell>
                                    {mostrarColsMedicos && (
                                      <>
                                        <TableCell>
                                          {medico ? especialidadeContrato(contrato) : '—'}
                                        </TableCell>
                                        <TableCell align="right">
                                          {medico
                                            ? (contrato.horasMensais != null &&
                                              String(contrato.horasMensais).trim() !== ''
                                                ? contrato.horasMensais
                                                : '—')
                                            : '—'}
                                        </TableCell>
                                        <TableCell align="right">
                                          {medico
                                            ? (contrato.quantidadeMedicos != null &&
                                              String(contrato.quantidadeMedicos).trim() !== ''
                                                ? contrato.quantidadeMedicos
                                                : '—')
                                            : '—'}
                                        </TableCell>
                                      </>
                                    )}
                                    <TableCell>
                                      <Box display="flex" alignItems="center">
                                        <Typography variant="body2">
                                          {contrato.numeroContrato}
                                        </Typography>
                                        <IconButton
                                          size="small"
                                          onClick={() => abrirPDFContrato(contrato)}
                                          sx={{ ml: 1 }}
                                        >
                                          <PictureAsPdfIcon fontSize="small" color="primary" />
                                        </IconButton>
                                      </Box>
                                    </TableCell>
                                    <TableCell>
                                      {aditivos.length === 0 ? (
                                        <Typography variant="body2" color="text.secondary">-</Typography>
                                      ) : (
                                        <Box display="flex" flexWrap="wrap" gap={0.5}>
                                          {aditivos.map((ad, idx) => (
                                            <Chip
                                              key={ad.id || idx}
                                              label={`${ad.numeroAditivo}°`}
                                              size="small"
                                              icon={<PictureAsPdfIcon />}
                                              onClick={() => abrirPDFAditivo(ad)}
                                              sx={{ 
                                                cursor: 'pointer',
                                                backgroundColor: ad.pdfPath ? 'primary.main' : 'grey.300',
                                                color: ad.pdfPath ? 'white' : 'text.secondary'
                                              }}
                                            />
                                          ))}
                                        </Box>
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      {formatCurrency(contrato.valorMes)}
                                    </TableCell>
                                    <TableCell>
                                      {formatDate(contrato.vigenciaFim)}
                                    </TableCell>
                                    <TableCell>
                                      <Box display="flex" alignItems="center">
                                        <Typography variant="body2">
                                          {dias}
                                        </Typography>
                                        {contrato.eventual && (
                                          <CheckCircleIcon 
                                            fontSize="small" 
                                            color="primary" 
                                            sx={{ ml: 1 }}
                                          />
                                        )}
                                      </Box>
                                    </TableCell>
                                    <TableCell>
                                      {(() => {
                                        const avaliacoes = avaliacoesPorContrato[contrato.numeroContrato] || [];
                                        return avaliacoes.length === 0 ? (
                                          <Typography variant="body2" color="text.secondary">-</Typography>
                                        ) : (
                                          <Box display="flex" flexDirection="column" gap={0.5}>
                                            {avaliacoes.map((avaliacao, idx) => (
                                              <Chip
                                                key={avaliacao.id || idx}
                                                label={`PDF(${avaliacao.nota}★)`}
                                                size="small"
                                                icon={<PictureAsPdfIcon />}
                                                onClick={() => abrirPDFAvaliacao(avaliacao)}
                                                sx={{ 
                                                  cursor: avaliacao.pdfPath ? 'pointer' : 'default',
                                                  backgroundColor: avaliacao.pdfPath ? getNotaColor(avaliacao.nota) === 'error' ? 'error.main' : 
                                                    getNotaColor(avaliacao.nota) === 'warning' ? 'warning.main' : 
                                                    getNotaColor(avaliacao.nota) === 'info' ? 'info.main' : 
                                                    getNotaColor(avaliacao.nota) === 'success' ? 'success.main' : 'primary.main' : 'grey.300',
                                                  color: avaliacao.pdfPath ? 'white' : 'text.secondary',
                                                  fontSize: '0.7rem'
                                                }}
                                              />
                                            ))}
                                          </Box>
                                        );
                                      })()}
                                    </TableCell>
                                    <TableCell>
                                      {(() => {
                                        const termos = termosDesignacaoPorContrato[contrato.numeroContrato] || [];
                                        return termos.length === 0 ? (
                                          <Typography variant="body2" color="text.secondary">-</Typography>
                                        ) : (
                                          <Box display="flex" flexDirection="column" gap={0.5}>
                                            {termos.map((termo, idx) => (
                                              <Chip
                                                key={termo.id || idx}
                                                label={Number(termo.assinado) === 1 ? 'Assinado' : 'PDF'}
                                                size="small"
                                                icon={<PictureAsPdfIcon />}
                                                onClick={() => abrirPDFTermoDesignacao(termo)}
                                                sx={{ 
                                                  cursor: termo.pdf_path ? 'pointer' : 'default',
                                                  backgroundColor: termo.pdf_path
                                                    ? (Number(termo.assinado) === 1 ? '#2e7d32' : '#4CAF50')
                                                    : 'grey.300',
                                                  color: termo.pdf_path ? 'white' : 'text.secondary',
                                                  fontSize: '0.7rem'
                                                }}
                                              />
                                            ))}
                                          </Box>
                                        );
                                      })()}
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}
                    </AccordionDetails>
                  </Accordion>
                </Card>
              );
            })
        )}
      </Box>

      {/* Botões para modais existentes — FISCAL: somente leitura (sem Adicionar) */}
      {!visaoFiscal && (
      <Box display="flex" gap={2} mt={4} flexWrap="wrap">
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setFiscaisModalOpen(true)}
          sx={{
            background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
            borderRadius: 2,
            px: 3,
            py: 1.5,
            boxShadow: '0 4px 12px rgba(25,118,210,0.3)',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 6px 16px rgba(25,118,210,0.4)'
            }
          }}
        >
          Gerenciar Fiscais
        </Button>
        
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => setDiretoresModalOpen(true)}
          sx={{
            borderColor: '#1976d2',
            color: '#1976d2',
            borderRadius: 2,
            px: 3,
            py: 1.5,
            transition: 'all 0.3s ease',
            '&:hover': {
              borderColor: '#1565c0',
              backgroundColor: 'rgba(25,118,210,0.05)',
              transform: 'translateY(-2px)'
            }
          }}
        >
          Adicionar Diretores e Gerentes Corporativos
        </Button>
        
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => setContratoGestaoModalOpen(true)}
          sx={{
            borderColor: '#1976d2',
            color: '#1976d2',
            borderRadius: 2,
            px: 3,
            py: 1.5,
            transition: 'all 0.3s ease',
            '&:hover': {
              borderColor: '#1565c0',
              backgroundColor: 'rgba(25,118,210,0.05)',
              transform: 'translateY(-2px)'
            }
          }}
        >
          Adicionar Contrato de Gestão
        </Button>

        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={async () => {
            try {
              if (!getToken()) {
                alert('Faça login para exportar a planilha.');
                return;
              }
              const res = await apiFetch('/api/relatorio-fiscais-contratos');
              if (res.status === 401) {
                alert('Sessão expirada. Faça login novamente para exportar.');
                return;
              }
              if (!res.ok) throw new Error('Erro ao gerar relatório');
              const data = await res.json();
              const rows = ['Fiscal (nome),Email,Setor,Unidade,Qtd contratos,Nº contrato,Empresa,Valor mensal,Vigência início,Vigência fim,Inconsistência'];
              const contratosPorFiscal = data.contratosPorFiscal || [];
              contratosPorFiscal.forEach(({ fiscal: f, contratos, qtdContratos }) => {
                const inconsistencia = qtdContratos === 0 ? 'Fiscal sem contrato' : 'OK';
                if (!contratos || contratos.length === 0) {
                  rows.push(`"${(f?.nome || '').replace(/"/g, '""')}","${(f?.email || '').replace(/"/g, '""')}","${(f?.setor || '').replace(/"/g, '""')}","${(f?.unidade || '').replace(/"/g, '""')}",0,,,,"","","",${inconsistencia}`);
                } else {
                  contratos.forEach((c, idx) => {
                    rows.push(`"${(f?.nome || '').replace(/"/g, '""')}","${(f?.email || '').replace(/"/g, '""')}","${(f?.setor || '').replace(/"/g, '""')}","${(f?.unidade || '').replace(/"/g, '""')}",${qtdContratos},"${(c.numeroContrato || '').replace(/"/g, '""')}","${(c.empresa || '').replace(/"/g, '""')}","${(c.valorMes || '')}","${(c.vigenciaInicio || '').replace(/"/g, '""')}","${(c.vigenciaFim || '').replace(/"/g, '""')}",${idx === 0 ? inconsistencia : ''}`);
                  });
                }
              });
              const csv = rows.join('\r\n');
              const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `fiscais_contratos_${new Date().toISOString().slice(0, 10)}.csv`;
              a.click();
              URL.revokeObjectURL(url);
            } catch (e) {
              console.error(e);
              alert('Erro ao exportar planilha. Verifique se está logado e com permissão.');
            }
          }}
          sx={{
            borderColor: '#2e7d32',
            color: '#2e7d32',
            borderRadius: 2,
            px: 3,
            py: 1.5,
            '&:hover': { borderColor: '#1b5e20', backgroundColor: 'rgba(46,125,50,0.05)' }
          }}
        >
          Exportar planilha (CSV)
        </Button>
      </Box>
      )}

      {/* Modal Gerenciar Fiscais */}
      <Dialog 
        open={fiscaisModalOpen} 
        onClose={() => setFiscaisModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
          color: 'white',
          borderRadius: '8px 8px 0 0'
        }}>
          Gerenciar Fiscais
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <TextField
              placeholder="Buscar fiscais..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              size="small"
              sx={{ width: 300 }}
            />
            {!visaoFiscal && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenModal()}
              sx={{
                background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                borderRadius: 2
              }}
            >
              Adicionar Fiscal
            </Button>
            )}
          </Box>
          
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ background: 'rgba(25,118,210,0.1)' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Nome</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Setor</TableCell>
                  {!visaoFiscal && <TableCell sx={{ fontWeight: 600 }}>Ações</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredData.filter(item => item.tipo === 'fiscal').map((fiscal) => (
                  <TableRow key={fiscal.id}>
                    <TableCell>{fiscal.nome}</TableCell>
                    <TableCell>{fiscal.email}</TableCell>
                    <TableCell>{fiscal.setor}</TableCell>
                    {!visaoFiscal && (
                    <TableCell>
                      <Box display="flex" gap={1}>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenModal(fiscal)}
                          sx={{ color: 'primary.main' }}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => openDeleteDialog(fiscal.id)}
                          sx={{ color: 'error.main' }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFiscaisModalOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* Modal Adicionar Fiscal */}
      <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="sm" fullWidth>
        <DialogTitle>Adicionar Fiscal</DialogTitle>
        <DialogContent>
          <TextField
            name="nome"
            label="Nome"
            value={form.nome}
            onChange={handleChange}
            fullWidth
            margin="normal"
          />
          <TextField
            name="email"
            label="Email"
            value={form.email}
            onChange={handleChange}
            fullWidth
            margin="normal"
          />
          <TextField
            name="setor"
            label="Setor"
            value={form.setor}
            onChange={handleChange}
            fullWidth
            margin="normal"
            select
            SelectProps={{ native: true }}
          >
            <option value="">Selecione um setor</option>
            {setores.map((setor) => (
              <option key={setor} value={setor}>
                {setor}
              </option>
            ))}
          </TextField>
          {formError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {formError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Cancelar</Button>
          <Button 
            onClick={handleSave} 
            variant="contained" 
            disabled={saving}
            sx={{
              background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)'
            }}
          >
            {saving ? <CircularProgress size={20} /> : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal Adicionar Diretores */}
      <Dialog 
        open={diretoresModalOpen} 
        onClose={() => setDiretoresModalOpen(false)}
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
          color: 'white',
          borderRadius: '8px 8px 0 0'
        }}>
          Adicionar Diretores e Gerentes Corporativos
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <TextField
            label="Nome"
            value={formDiretor.nome}
            onChange={(e) => setFormDiretor({ ...formDiretor, nome: e.target.value })}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Email"
            value={formDiretor.email}
            onChange={(e) => setFormDiretor({ ...formDiretor, email: e.target.value })}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Cargo"
            value={formDiretor.cargo}
            onChange={(e) => setFormDiretor({ ...formDiretor, cargo: e.target.value })}
            fullWidth
            margin="normal"
            select
            SelectProps={{ native: true }}
          >
            <option value="">Selecione um cargo</option>
            {cargos.map((cargo) => (
              <option key={cargo} value={cargo}>
                {cargo}
              </option>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDiretoresModalOpen(false)}>Cancelar</Button>
          <Button 
            onClick={handleSaveDiretor} 
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)'
            }}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal Adicionar Contrato de Gestão */}
      <Dialog 
        open={contratoGestaoModalOpen} 
        onClose={() => setContratoGestaoModalOpen(false)}
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
          color: 'white',
          borderRadius: '8px 8px 0 0'
        }}>
          Adicionar Contrato de Gestão
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <TextField
            label="N° do Contrato"
            value={formContratoGestao.numeroContrato}
            onChange={(e) => setFormContratoGestao({ ...formContratoGestao, numeroContrato: e.target.value })}
            fullWidth
            margin="normal"
          />
          <TextField
            label="N° do Aditivo"
            value={formContratoGestao.numeroAditivo}
            onChange={(e) => setFormContratoGestao({ ...formContratoGestao, numeroAditivo: e.target.value })}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Valor Total Recebido"
            value={formContratoGestao.valorTotalRecebido}
            onChange={(e) => {
              const value = e.target.value;
              // Remove tudo que não é número ou ponto/vírgula
              const cleanValue = value.replace(/[^\d.,]/g, '');
              // Substitui vírgula por ponto para padronizar
              const normalizedValue = cleanValue.replace(',', '.');
              setFormContratoGestao({ ...formContratoGestao, valorTotalRecebido: normalizedValue });
            }}
            fullWidth
            margin="normal"
            placeholder="Ex: 1980000.00"
            helperText="Digite apenas números (ex: 1980000.00)"
          />
          <TextField
            label="Valor Destinado ao Setor de Contratos"
            value={formContratoGestao.valorDestinadoSetor}
            onChange={(e) => {
              const value = e.target.value;
              // Remove tudo que não é número ou ponto/vírgula
              const cleanValue = value.replace(/[^\d.,]/g, '');
              // Substitui vírgula por ponto para padronizar
              const normalizedValue = cleanValue.replace(',', '.');
              setFormContratoGestao({ ...formContratoGestao, valorDestinadoSetor: normalizedValue });
            }}
            fullWidth
            margin="normal"
            placeholder="Ex: 500000.00"
            helperText="Digite apenas números (ex: 500000.00)"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setContratoGestaoModalOpen(false)}>Cancelar</Button>
          <Button 
            onClick={handleSaveContratoGestao} 
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)'
            }}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de confirmação de exclusão */}
      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog}>
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <Typography>Tem certeza que deseja excluir este fiscal?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog}>Cancelar</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
} 
